// Teams screen: list the active org's teams, create/edit/delete them, and open
// the composition drawer (members + granted roles). Teams organize users so a set
// of roles can be granted to many people at once — every member inherits the
// team's roles.

import { useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { TeamDto } from "../api/types.js"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { TeamFormDrawer } from "../components/TeamFormDrawer.js"
import { TeamMembersDrawer } from "../components/TeamMembersDrawer.js"
import { fr } from "../i18n/fr.js"
import { messageForError } from "../lib/errors.js"
import { useAsync } from "../lib/useAsync.js"
import { useOrg } from "../state/org.js"
import { ConfirmDialog } from "../ui/ConfirmDialog.js"
import { PencilIcon, TrashIcon, UsersIcon } from "../ui/icons.js"
import { Alert, Button, IconButton, Spinner } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"

type FormState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; team: TeamDto }

export function TeamsPage() {
  const notify = useNotify()
  const writesEnabled = useWritesEnabled()
  const { activeOrg } = useOrg()

  const teams = useAsync<TeamDto[]>(
    () => (activeOrg ? adminApi.listTeams(activeOrg) : Promise.resolve([])),
    [activeOrg]
  )

  const [form, setForm] = useState<FormState>({ kind: "closed" })
  const [manageTeamId, setManageTeamId] = useState<string | null>(null)
  const [toDelete, setToDelete] = useState<TeamDto | null>(null)
  const [deleting, setDeleting] = useState(false)

  async function confirmDelete() {
    if (!toDelete) return
    setDeleting(true)
    try {
      await adminApi.deleteTeam(toDelete.id)
      notify.success(fr.teams.remove.success)
      setToDelete(null)
      teams.reload()
    } catch (err) {
      notify.error(messageForError(err, fr.teams.create.errorGeneric))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{fr.teams.title}</h1>
          <p>{fr.teams.description}</p>
        </div>
        <Button
          variant="primary"
          disabled={!writesEnabled || !activeOrg}
          onClick={() => setForm({ kind: "create" })}
        >
          {fr.teams.createButton}
        </Button>
      </div>

      {!activeOrg ? <Alert>{fr.teams.noOrg}</Alert> : null}

      {teams.loading ? <Spinner /> : null}
      {teams.error ? <Alert variant="error">{fr.teams.error}</Alert> : null}

      {activeOrg && teams.data && !teams.loading ? (
        teams.data.length === 0 ? (
          <Alert>{fr.teams.empty}</Alert>
        ) : (
          <div className="table-wrap">
            <table className="table table--teams">
              <thead>
                <tr>
                  <th>{fr.teams.columns.name}</th>
                  <th className="table__num">{fr.teams.columns.members}</th>
                  <th className="table__num">{fr.teams.columns.roles}</th>
                  <th className="table__actions">{fr.teams.columns.actions}</th>
                </tr>
              </thead>
              <tbody>
                {teams.data.map((team) => (
                  <tr key={team.id}>
                    <td>
                      <div>{team.name}</div>
                      {team.description ? <div className="subtitle">{team.description}</div> : null}
                    </td>
                    <td className="table__num">{team.memberCount}</td>
                    <td className="table__num">{team.roleCount}</td>
                    <td className="table__actions">
                      <div className="table__actions-row">
                        <IconButton
                          label={fr.teams.manage.button}
                          onClick={() => setManageTeamId(team.id)}
                        >
                          <UsersIcon />
                        </IconButton>
                        <IconButton
                          label={fr.teams.edit.button}
                          disabled={!writesEnabled}
                          onClick={() => setForm({ kind: "edit", team })}
                        >
                          <PencilIcon />
                        </IconButton>
                        <IconButton
                          label={fr.teams.remove.button}
                          variant="danger"
                          disabled={!writesEnabled}
                          onClick={() => setToDelete(team)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {form.kind !== "closed" && activeOrg ? (
        <TeamFormDrawer
          mode={form.kind}
          org={activeOrg}
          team={form.kind === "edit" ? form.team : undefined}
          onClose={() => setForm({ kind: "closed" })}
          onSaved={() => teams.reload()}
        />
      ) : null}

      {manageTeamId && activeOrg ? (
        <TeamMembersDrawer
          teamId={manageTeamId}
          org={activeOrg}
          onClose={() => setManageTeamId(null)}
          onSaved={() => teams.reload()}
        />
      ) : null}

      <ConfirmDialog
        open={toDelete !== null}
        title={fr.teams.remove.dialogTitle}
        message={fr.teams.remove.dialogMessage}
        confirmText={fr.teams.remove.confirm}
        cancelText={fr.app.cancel}
        submitting={deleting}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  )
}
